/**
 * Layout primitives shared by the home page and the service landing pages.
 *
 * These lived inside page.tsx until the service pages needed the same gutter,
 * eyebrow and section heading. Copying them would have let the two drift.
 */

export function Container({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`mx-auto w-full max-w-[1200px] px-6 ${className}`}>
      {children}
    </div>
  );
}

export function Eyebrow({ children }: { children: React.ReactNode }) {
  return <p className="bw-eyebrow text-ink-500">{children}</p>;
}

export function SectionHead({
  eyebrow,
  title,
  lead,
  align = 'center',
  as: Title = 'h2',
  eyebrowAs: EyebrowTag = 'p',
}: {
  eyebrow: string;
  title: string;
  lead?: string;
  align?: 'center' | 'left';
  /** Tag for the display line. The level is the caller's choice -- the styling
   *  is identical either way. Pass 'p' when the eyebrow carries the heading,
   *  so the page does not end up with a heading that labels no section. */
  as?: 'h1' | 'h2' | 'p';
  /** Tag for the eyebrow. Service pages make it the h1: the service name is
   *  what the page is actually about, and the line beneath it is a tagline. */
  eyebrowAs?: 'h1' | 'p';
}) {
  const alignment =
    align === 'center' ? 'text-center mx-auto items-center' : 'text-left';
  return (
    <div className={`flex flex-col gap-3 ${alignment}`}>
      <EyebrowTag className="bw-eyebrow text-ink-500">{eyebrow}</EyebrowTag>
      <Title className="bw-display text-[34px] leading-[1.15] text-bw-ink md:text-[44px]">
        {title}
      </Title>
      {lead ? (
        <p className="max-w-[680px] text-[17px] leading-[1.6] text-ink-700">
          {lead}
        </p>
      ) : null}
    </div>
  );
}
